/**
 * Logger Utility
 * Controls logging verbosity based on environment and build state
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerConfig {
  enableConsoleLogging: boolean;
  enableBuildLogging: boolean;
  minLevel: LogLevel;
}

class Logger {
  private config: LoggerConfig;
  private isBuilding: boolean;

  constructor() {
    this.isBuilding = process.env.NODE_ENV === 'production' || process.env.NEXT_PHASE === 'phase-production-build';
    
    this.config = {
      enableConsoleLogging: process.env.NODE_ENV === 'development',
      enableBuildLogging: process.env.VERBOSE_BUILD === 'true',
      minLevel: this.isBuilding ? 'warn' : 'debug'
    };
  }

  private shouldLog(level: LogLevel): boolean {
    if (this.isBuilding && !this.config.enableBuildLogging) {
      // During build, log info/warn/error but suppress debug unless explicitly enabled
      return level === 'info' || level === 'warn' || level === 'error';
    }
    
    if (!this.config.enableConsoleLogging && level === 'debug') {
      return false;
    }

    const levels: Record<LogLevel, number> = {
      debug: 0,
      info: 1,
      warn: 2,
      error: 3
    };

    return levels[level] >= levels[this.config.minLevel];
  }

  debug(message: string, ...args: unknown[]): void {
    if (this.shouldLog('debug')) {
      console.log(`🔍 ${message}`, ...args);
    }
  }

  info(message: string, ...args: unknown[]): void {
    if (this.shouldLog('info')) {
      console.log(`ℹ️ ${message}`, ...args);
    }
  }

  warn(message: string, ...args: unknown[]): void {
    if (this.shouldLog('warn')) {
      console.warn(`⚠️ ${message}`, ...args);
    }
  }

  error(message: string, ...args: unknown[]): void {
    if (this.shouldLog('error')) {
      console.error(`❌ ${message}`, ...args);
    }
  }

  // Service-specific loggers
  service(serviceName: string) {
    return {
      debug: (message: string, ...args: unknown[]) => this.debug(`[${serviceName}] ${message}`, ...args),
      info: (message: string, ...args: unknown[]) => this.info(`[${serviceName}] ${message}`, ...args),
      warn: (message: string, ...args: unknown[]) => this.warn(`[${serviceName}] ${message}`, ...args),
      error: (message: string, ...args: unknown[]) => this.error(`[${serviceName}] ${message}`, ...args)
    };
  }

  // Firebase-specific logger
  firebase(component: string) {
    return {
      debug: (message: string, ...args: unknown[]) => this.debug(`🔥 [Firebase-${component}] ${message}`, ...args),
      info: (message: string, ...args: unknown[]) => this.info(`🔥 [Firebase-${component}] ${message}`, ...args),
      warn: (message: string, ...args: unknown[]) => this.warn(`🔥 [Firebase-${component}] ${message}`, ...args),
      error: (message: string, ...args: unknown[]) => this.error(`🔥 [Firebase-${component}] ${message}`, ...args)
    };
  }
}

// Export singleton logger instance
export const logger = new Logger();

// Convenience exports
export const serviceLogger = (serviceName: string) => logger.service(serviceName);
export const firebaseLogger = (component: string) => logger.firebase(component); 
